import { action, computed, observable } from 'mobx';
import { BaseBlock } from '~/models/blocks/base-block.model';
import {
    AutoPayStatus,
    IPaymentBlockResponse,
    IPaymentVariableValue
} from '~/types/server.types';
import {
    AmountCalcType,
    DateCalcType,
    FlowStatus,
    IPaymentContent,
    PaymentStatus
} from '~/types/model.types';
import { Variable } from '~/models/variable.model';
import { daysDifference, formatDate } from '~/utils/date.utils';
import type { PaymentScheduleBlock } from '~/models/blocks/payment-schedule-block.model';
import { toFixedIfNotRound } from '~/utils/money.utils';
import type { InvoiceBlock } from '~/models/blocks/invoice-block.model';
import { getPaymentDateDisplayValue } from '~/components/common/InvoiceBlock/invoice.utils';
import type { RecurringScheduleBlock } from '~/models/blocks/recurring-schedule-block.model';
import {
    NUMBER_OF_DAYS_CONSIDER_PAID,
    PaymentMethodType
} from '~/types/pay.types';
import type { FlowRevision } from '~/models/flow-revision.model';
import type { RootStore } from '~/stores/root.store';
import { calculateLateFee } from '~/components/Payments/utils';

type PaymentBlockDataForServer = ReturnType<BaseBlock['getDataForServer']> &
    Pick<
        IPaymentBlockResponse,
        'type' | 'payment_content' | 'connected_variable_id'
    >;

export class PaymentBlock extends BaseBlock {
    type: 'payment_block';
    @observable payment_content: IPaymentContent;
    @observable connected_variable_id: string;
    @observable date_issued: string;

    constructor(
        block: IPaymentBlockResponse,
        flowRevision: FlowRevision<RootStore>,
        rootStore: RootStore
    ) {
        super(block, flowRevision, rootStore);
        this.type = block.type;
    }

    @computed
    get totalAmount() {
        return (this.paymentAmount ?? 0) + this.lateFee + this.tipPaid;
    }

    @computed
    get paymentAmount() {
        const value = this.connectedVariable?.value;
        if (!value) {
            return null;
        }

        return value.amount;
    }

    @computed
    get paymentAmountPaid() {
        const value = this.connectedVariable?.value;
        if (!value) {
            return null;
        }

        return value.amount_paid;
    }

    @computed
    get netPayAmount() {
        if (this.isPartiallyRefunded) {
            return this.paymentAmountPaid - this.refundedAmount - this.lateFee;
        } else if (this.isRefunded) {
            return 0;
        }

        return this.paymentAmount;
    }

    @computed
    get instantPayoutFee() {
        return this.connectedVariable?.value?.instant_payout_theoretical_fee;
    }

    @computed
    get paymentIndex() {
        return this.connectedVariable?.value?.payment_index;
    }

    @computed
    get is_paid() {
        return this.connectedVariable?.value?.is_paid;
    }

    @computed
    get is_refunded() {
        return this.connectedVariable?.value?.is_refunded;
    }

    @computed
    get isDeletable() {
        if (
            this.flow.isLeadForm ||
            this.isPaid ||
            this.isRefunded ||
            !(this.paymentScheduleBlock?.childBlocks.length > 1)
        ) {
            return false;
        }
        return true;
    }

    @computed
    get isEditable() {
        return (
            !this.isPaid &&
            !this.isRefunded &&
            !(this.parent as RecurringScheduleBlock)?.isRecurringInvoice
        );
    }

    @computed
    get paymentPercents() {
        if (this.isPercentageBased) {
            return toFixedIfNotRound(this.payment_content?.percentage);
        }

        const { totalAmount } = this.paymentScheduleBlock;
        return totalAmount
            ? toFixedIfNotRound((this.paymentAmount / totalAmount) * 100, 0)
            : 0;
    }

    @computed
    get paymentId() {
        return this.connectedVariable?.value?.payment_id;
    }

    @computed
    get isFixed() {
        return this.amountCalcType === AmountCalcType.Fixed;
    }

    @computed
    get remainedMaxCustom() {
        const grandTotalLimit = this.invoiceBlock.hasPaidPayments
            ? this.invoiceBlock.invoiceSummaryBlock.grandTotal -
              this.paymentScheduleBlock.totalPaid
            : this.invoiceBlock.invoiceSummaryBlock.minimalUnpaidGrandTotal;
        return Number(
            (
                grandTotalLimit -
                this.paymentScheduleBlock.fixedUnpaidPaymentsAmount +
                (this.isFixed ? this.paymentAmount : 0)
            ).toFixed(2)
        );
    }

    @computed
    get isPercentageBased() {
        return [
            AmountCalcType.Percentage,
            AmountCalcType.DivideEqually
        ].includes(this.amountCalcType);
    }

    @computed
    get amountCalcType() {
        return this.payment_content?.amount_calc_type;
    }

    @computed
    get milestoneTitle() {
        return this.payment_content?.milestone_title;
    }

    @computed
    get paymentDueDate() {
        return this.connectedVariable?.value?.due_date;
    }

    @computed
    get isPaymentDueDateTbd() {
        return this.connectedVariable?.value?.is_tbd;
    }

    @computed
    get transactionId() {
        const value = this.connectedVariable?.value;
        if (!value) {
            return null;
        }
        return value.charge_id;
    }

    @computed
    get refundId() {
        const value = this.connectedVariable?.value;
        if (!value) {
            return null;
        }

        return value.refund_data?.refund_transaction_id;
    }

    @computed
    get isAutoPaid() {
        const value = this.connectedVariable?.value;
        if (!value) {
            return null;
        }
        return value.is_auto_paid;
    }

    @computed
    get isAutoPay() {
        if (this.isPaid || this.isRefunded) {
            return this.isAutoPaid;
        }
        return this.invoiceBlock.chargeBlock?.isAutoPayActive;
    }

    @computed
    get shouldShowAutopayIndication() {
        if (this.isPaid || this.isRefunded) {
            return this.isAutoPaid;
        } else {
            const autoPayStatus = this.invoiceBlock.chargeBlock?.autoPayStatus;
            return (
                autoPayStatus === AutoPayStatus.Active ||
                autoPayStatus === AutoPayStatus.Paused
            );
        }
    }

    @computed
    get autoPayStatus() {
        return this.invoiceBlock.chargeBlock?.autoPayStatus;
    }

    @computed
    get isAutoPayDeclined() {
        return !this.isPaid && this.latestPaymentAction?.source === 'autopay';
    }

    @computed
    get paymentChargeDate() {
        const value = this.connectedVariable?.value;
        if (!value) {
            return null;
        }
        return value.charge_date || value.pending_mark_as_paid?.charge_date;
    }

    @computed
    get paymentRefundDate() {
        const value = this.connectedVariable?.value;
        if (!value) {
            return null;
        }
        return value.last_refund_timestamp;
    }

    @computed
    get refundedAmount() {
        const value = this.connectedVariable?.value;
        if (!value) {
            return null;
        }
        return (
            value.refunded_amount ||
            value.pending_mark_as_refunded?.refunded_amount ||
            value.amount_to_refund ||
            0
        );
    }

    @computed
    get transferredToVendor() {
        const value = this.connectedVariable?.value;
        if (!value) {
            return null;
        }
        return value.transferred_to_vendor;
    }

    @computed
    get invoiceId() {
        return this.connectedVariable?.value?.invoice_id;
    }

    @computed
    get recurringInvoiceNumber() {
        return this.connectedVariable?.value?.invoice_header_snapshot
            ?.invoice_number;
    }

    @computed
    get tipPaid() {
        if (this.isPaid || this.isPending || this.isRefunded) {
            // it is possible that tip was saved but payment eas not successful
            // it is hard to catch and fix this state in the backend, so we added this safeguard
            return this.connectedVariable?.value?.tip_paid || 0;
        }
        return 0;
    }

    @computed
    get lateFee() {
        return calculateLateFee(this);
    }

    @computed
    get chargeNotes() {
        return this.connectedVariable?.value?.charge_notes || '';
    }

    @computed
    get refundNotes() {
        return this.connectedVariable?.value?.refund_notes || '';
    }

    get reminders() {
        return this.connectedVariable?.value?.reminders;
    }

    get chargeDescriptor() {
        return this.connectedVariable?.value?.charge_statement_descriptor;
    }

    get paymentPayerEmail() {
        return this.connectedVariable?.value?.payment_payer_email;
    }

    get paymentPayerName() {
        return this.connectedVariable?.value?.payment_payer_name;
    }

    get paymentMethodName() {
        return this.connectedVariable?.value?.payment_method_name;
    }

    get paymentMethodType() {
        return this.connectedVariable?.value?.payment_method_type;
    }

    get paymentMethodTypeName() {
        return this.connectedVariable?.value?.payment_method_type_name;
    }

    get paymentMethodLast4() {
        return this.connectedVariable?.value?.payment_method_last4;
    }

    get isDependOnSelection() {
        return (
            this.invoiceBlock.hasIncompleteServiceSelections &&
            !this.isFixed &&
            !this.isPaid &&
            !this.isRefunded
        );
    }

    get isPaid() {
        const value = this.connectedVariable?.value;
        if (!value) {
            return false;
        }

        return (
            !this.isRefunded && (value.is_paid || !!value.pending_mark_as_paid)
        );
    }

    @computed
    get isPendingMAP() {
        return !!this.connectedVariable?.value?.pending_mark_as_paid;
    }

    @computed
    get isPendingRefundOrUndoMAP() {
        return (
            !!this.connectedVariable?.value?.pending_mark_as_refunded ||
            !!this.connectedVariable?.value?.pending_undo_mark_as_paid
        );
    }

    get isPaidWithoutPendingMAP() {
        const value = this.connectedVariable?.value;
        if (!value) {
            return false;
        }

        return !this.isRefunded && value.is_paid && !value.pending_mark_as_paid;
    }

    get isManualPaid() {
        const value = this.connectedVariable?.value;
        if (!value) {
            return false;
        }
        return value.is_paid && value.is_manual;
    }

    get isRefunded() {
        const value = this.connectedVariable?.value;
        if (!value) {
            return false;
        }

        return !!value.is_refunded || !!value.pending_mark_as_refunded;
    }

    get isFullyRefunded() {
        return this.paymentStatus === PaymentStatus.Refunded;
    }

    @computed
    get isPaymentHandled() {
        return [
            PaymentStatus.Paid,
            PaymentStatus.Refunded,
            PaymentStatus.Refunding,
            PaymentStatus.PartiallyRefunded,
            PaymentStatus.PendingRefund,
            PaymentStatus.DisputePending,
            PaymentStatus.DisputeReserved,
            PaymentStatus.DisputeReservedLost,
            PaymentStatus.DisputeWon,
            PaymentStatus.DisputeLost,
            PaymentStatus.DisputePartiallyWon,
            PaymentStatus.DisputeWonRefunded,
            PaymentStatus.DisputeWonPartiallyRefunded
        ].includes(this.paymentStatus);
    }

    get isOverDue() {
        return this.paymentStatus === PaymentStatus.Overdue;
    }

    get isPending() {
        return this.connectedVariable?.value?.is_pending;
    }

    get isAch() {
        return this.paymentMethodType === PaymentMethodType.BankAccount;
    }

    get isFailedAch() {
        return (
            this.isAch &&
            !this.isPaymentHandled &&
            !this.isPending &&
            !this.isPaid
        );
    }

    get isCreditCard() {
        return this.paymentMethodType === PaymentMethodType.CreditCard;
    }

    get isStripeDepositDate() {
        return (
            !!this.depositDate &&
            this.connectedVariable?.value?.is_stripe_deposit_date
        );
    }

    get isInstantDepositOn() {
        return this.connectedVariable?.value?.is_instant_deposit;
    }

    get currency() {
        return this.flow.currency;
    }

    get isCanceled() {
        return (
            this.flow.expired ||
            (this.invoiceBlock.isRecurringInvoice &&
                !this.recurringScheduleBlock.isActive)
        );
    }

    get invoiceBlock() {
        return this.parent.parent as InvoiceBlock;
    }

    get paymentScheduleBlock() {
        return this.parent as PaymentScheduleBlock;
    }

    get recurringScheduleBlock() {
        return this.parent as RecurringScheduleBlock;
    }

    get isPartiallyRefunded() {
        return (
            this.paymentAmount &&
            this.refundedAmount &&
            this.paymentAmount > this.refundedAmount
        );
    }

    @computed
    get isLateFeeWaived() {
        return !!this.connectedVariable?.value?.late_fee_waived_ts;
    }

    @computed
    get totalPaymentsCount() {
        return this.paymentScheduleBlock.paymentBlocks.length;
    }

    @computed
    get paymentOrderNumber() {
        return (
            this.paymentScheduleBlock.paymentBlocks.findIndex(
                paymentBlock => paymentBlock.block_id === this.block_id
            ) + 1
        );
    }

    @computed
    get isMilestone() {
        if (this.payment_content) {
            return (
                this.payment_content.date_calc_type === DateCalcType.Milestone
            );
        }
        return false;
    }

    @computed
    get isUnpaidMilestone() {
        return (
            this.isMilestone &&
            !!this.milestoneCompletedOn &&
            !this.isPaid &&
            !this.isRefunded
        );
    }

    @computed
    get shouldShowRequestPayment() {
        return (
            this.isMilestone &&
            this.flow.status !== FlowStatus.Draft &&
            !this.milestoneCompletedOn &&
            !this.isPaid
        );
    }

    @computed
    get shouldShowMarkAsPaid() {
        return (
            ((this.paymentStatus === PaymentStatus.Upcoming ||
                this.paymentStatus === PaymentStatus.Overdue ||
                this.paymentStatus === PaymentStatus.Unpaid) &&
                this.isNextPayment) ||
            this.shouldShowRequestPayment
        );
    }

    @computed
    get isEligibleForReminder() {
        if (this.flow.expired || this.flow.status === FlowStatus.Draft) {
            return false;
        }

        return (
            (this.isNextPayment && !this.isMilestone) || this.isUnpaidMilestone
        );
    }

    @computed
    get shouldShowAddBankAccount() {
        return (
            this.isPaymentHandled &&
            !this.isManualPaid &&
            !this.rootStore.companyStore.currCompany
                .bank_account_payment_method_id
        );
    }

    @computed
    get shouldShowFixBankAccount() {
        return (
            this.isPaymentHandled &&
            !this.isManualPaid &&
            this.rootStore.companyStore.currCompany?.fields_to_fix?.length > 0
        );
    }

    @computed
    get wasReminderSent() {
        return this.connectedVariable?.value?.reminders?.length > 0;
    }

    @computed
    get milestoneCompletedOn() {
        return this.connectedVariable?.value?.milestone_completed_on;
    }

    @computed
    get latestPaymentAction() {
        return this.connectedVariable?.value?.payment_action_status;
    }

    @computed
    get paymentStatus() {
        if (
            this.isCanceled &&
            !this.isPaid &&
            !this.isRefunded &&
            !this.isPartiallyRefunded
        ) {
            return PaymentStatus.Canceled;
        }
        if (this.flow.isLeadForm) {
            return PaymentStatus.DueToday;
        }
        const paymentVariable = this.connectedVariable;
        return (
            paymentVariable?.value?.payment_status_label ||
            PaymentStatus.Upcoming
        );
    }

    @computed
    get paymentStatusTracking() {
        return this.connectedVariable?.value?.payment_status_tracking || [];
    }

    @computed
    get connectedDueDateToShow() {
        return getPaymentDateDisplayValue({
            date: this.paymentDueDate,
            paymentVariable: this.connectedVariable?.value,
            paymentContent: this.payment_content || {
                date_calc_type: DateCalcType.Fixed
            },
            isRecurring: this.invoiceBlock?.isRecurringInvoice,
            isTemplate: this.flow?.is_template || this.flow?.isLeadForm
        });
    }

    @computed
    get calculatedDueDateText() {
        let dateToShow = this.connectedDueDateToShow;
        if (!!dateToShow && this.isMilestone && !!this.milestoneCompletedOn) {
            dateToShow = `${formatDate(
                this.milestoneCompletedOn,
                'MM D, YYYY'
            )} (${dateToShow})`;
        }
        return !!dateToShow ? dateToShow : '- - -';
    }

    @computed
    get isPaymentHighlighted() {
        const dueDate = new Date(this.connectedVariable?.value?.due_date);
        const shouldBePaidToday =
            this.paymentStatus === PaymentStatus.Upcoming &&
            daysDifference(dueDate, new Date()) === 0;

        if (this.paymentStatus === PaymentStatus.Overdue || shouldBePaidToday) {
            return true;
        }

        return false;
    }

    @computed
    get isNextPayment() {
        if (this.parent.type === 'payment_schedule_block') {
            return (
                (this.parent as PaymentScheduleBlock).nextPaymentBlock
                    ?.block_id === this.block_id
            );
        }
        return (
            (this.parent as RecurringScheduleBlock).nextPaymentBlock
                ?.block_id === this.block_id
        );
    }

    get connectedVariable(): Variable<IPaymentVariableValue> {
        return super.connectedVariable;
    }

    @computed
    get allowInstantPayout() {
        return this.connectedVariable?.value?.allow_instant_payout;
    }

    @computed
    get depositDate() {
        return this.connectedVariable?.value?.estimated_deposit_date;
    }

    @computed
    get isConsideredMemberPaid() {
        return (
            this.isStripeDepositDate &&
            daysDifference(
                this.connectedVariable?.value?.estimated_deposit_date,
                new Date()
            ) < NUMBER_OF_DAYS_CONSIDER_PAID
        );
    }

    @computed
    get shouldExpandOnSelect() {
        return false;
    }

    @computed
    get dateCalcType() {
        return this.payment_content.date_calc_type;
    }

    isDraggable() {
        return false;
    }

    dndAcceptedTypes() {
        return null;
    }

    getDataForServer(): PaymentBlockDataForServer {
        return {
            ...super.getDataForServer(),
            type: this.type as 'payment_block',
            payment_content: { ...this.payment_content },
            // this is a ad hoc solution for the zombie payment wrappers
            // Please use this code only if you have issues with variables ids changing on restore
            // https://honeybook.atlassian.net/browse/CORE-12247
            connected_variable_id: this.connected_variable_id
        };
    }

    @action
    mergeBlockAttributes(block, attributes) {
        super.mergeBlockAttributes(block, attributes);

        if (
            'connected_variable_id' in attributes &&
            block.connected_variable_id
        ) {
            this.connected_variable_id = block.connected_variable_id;
        }

        if (block.payment_content) {
            this.payment_content = block.payment_content;
        }
    }
}
